import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import getLayers from "./getLayer";

import { LayerGroupName } from "types/dataModel";
import { useFilterContext } from "hooks/useFilters";
import usePMTiles from "hooks/usePMTiles";
import { layersByGroup } from "layers/layers";

export function useLayers(
  map,
  mapLoaded,
  init_layer_group,
  style,
  providedLayersToggle,
) {
  /**
   * Maintains the loaded sources and layers for a MapboxGL Map.  Allows for switching of groups of layers.
   * @param map  MapboxGL map
   * @param mapLoaded   Boolean state watcher to ensure the map is ready
   * @param init_layer_group Initial layer group to use
   * @param style  Map style.  React State from useMap.  Layers must be reloaded when the style changes.
   * @param layersToggle  A state to toggle layers on and off
   * @return {Object} layerGroup, layerSelectionDependencies, subgroup, subgroupOn, setLayerGroup, setSubgroup, layersToggle, toggleLayer
   */
  const [layerGroup, setLayerGroup] = useState(init_layer_group);
  const [subgroup, setSubgroup] = useState("");
  const [subgroupOn, setSubgroupOn] = useState(false);
  const layersRef = useRef([]);
  const viewportLockTimeout = useRef();
  const pmtilesSources = usePMTiles();

  const [layersToggle, setLayersToggle] = useState({});
  const layerToggleToUse = useMemo(
    () => providedLayersToggle ?? layersToggle,
    [providedLayersToggle, layersToggle],
  );
  const availableLayers = useMemo(
    () => layersByGroup[layerGroup],
    [layerGroup],
  );

  useEffect(() => {
    if (Array.isArray(availableLayers) || providedLayersToggle) return;
    const newLayersToggle = {
      ...Object.keys(availableLayers).reduce((acc, layer) => {
        const key = availableLayers[layer]?.toggleKey ?? layer;
        if (availableLayers[layer]?.toggleKey) {
          const keys = Object.keys(availableLayers).filter(
            (l) => availableLayers[l]?.toggleKey === key,
          );
          acc[key] = keys.some((k) => acc[k]);
        } else if (availableLayers[layer]?.slideKey) {
          const hasAnyTrueInGroup = Object.entries(acc).some(
            ([k, v]) =>
              availableLayers[k]?.slideKey ===
                availableLayers[layer]?.slideKey && v,
          );
          acc[key] =
            hasAnyTrueInGroup ||
            availableLayers[layer]?.slidePosition === "left";
        } else if (availableLayers[layer]?.selectKey) {
          acc[key] =
            Object.entries(acc).findIndex(
              ([k, v]) =>
                availableLayers[k]?.selectKey ===
                  availableLayers[layer]?.selectKey && v,
            ) === -1 ||
            Object.entries(acc).findIndex(
              ([k, v]) =>
                availableLayers[k]?.selectPosition ===
                  availableLayers[layer]?.selectPosition && v,
            ) !== -1;
        } else {
          acc[key] = true;
        }
        return acc;
      }, {}),
      ...layersToggle,
    };
    setLayersToggle(newLayersToggle);
    // React wants to add layersToggle, don't!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLayers, providedLayersToggle]);

  const toggleLayer = useCallback(
    (layer) => {
      if (providedLayersToggle) return;
      setLayersToggle((prev) => {
        const newToggleState = { ...prev, [layer]: !prev[layer] };
        const layerConfig = availableLayers[layer];
        const sharedToggleKey = layerConfig?.toggleKey;
        const selectKey = layerConfig?.selectKey;
        if (sharedToggleKey) {
          Object.keys(availableLayers).forEach((l) => {
            if (availableLayers[l]?.toggleKey === sharedToggleKey) {
              newToggleState[l] = newToggleState[layer];
            }
          });
        } else if (!layerConfig) {
          // `layer` is a toggle group key (e.g. "mangrove_extent"), not a LayerName.
          // Sync all layers that share this toggleKey.
          Object.keys(availableLayers).forEach((l) => {
            if (availableLayers[l]?.toggleKey === layer) {
              newToggleState[l] = newToggleState[layer];
            }
          });
        } else if (selectKey) {
          Object.keys(availableLayers).forEach((l) => {
            if (
              availableLayers[l]?.selectKey === selectKey &&
              availableLayers[l]?.selectPosition === layerConfig?.selectPosition
            ) {
              newToggleState[l] = newToggleState[layer];
            }
          });
        }
        return newToggleState;
      });
    },
    [availableLayers, providedLayersToggle],
  );

  const { activeFilters: filters } = useFilterContext();

  const layers_and_legends = useMemo(() => {
    const loggedLayers = Object.keys(layerToggleToUse).filter(
      (layer) => layerToggleToUse[layer] && !availableLayers[layer]?.toggleKey,
    );

    return getLayers(
      layerGroup,
      { floodGroup: subgroup || loggedLayers[1] },
      layersToggle,
    );
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    layerToggleToUse,
    layerGroup,
    subgroup,
    filters,
    availableLayers,
    layersToggle,
  ]);

  useEffect(() => {
    if (mapLoaded) {
      if (layerGroup === LayerGroupName.RiskReduction) {
        clearTimeout(viewportLockTimeout.current);
        map.setMaxPitch(75);
        map.dragRotate.enable();
      } else {
        map.setPitch(0);
        map.rotateTo(0, { animationDuration: 2000 });
        viewportLockTimeout.current = setTimeout(() => {
          map.setMaxPitch(0);
          map.dragRotate.disable();
        }, 2000);
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [layerGroup, mapLoaded]);

  const layers = useMemo(
    () =>
      !Array.isArray(availableLayers)
        ? layers_and_legends.layers.filter((layer) => {
            const toggleKey = availableLayers[layer.key]?.toggleKey;
            if (
              availableLayers[layer.key] === true ||
              !availableLayers[layer.key]
            ) {
              return true;
            }

            if (availableLayers[layer.key].slidePosition) {
              const associatedLayerName = Object.keys(availableLayers).find(
                (layerName) =>
                  availableLayers[layerName]?.slidePosition ===
                    availableLayers[layer.key].slidePosition &&
                  !!availableLayers[layerName]?.slideKey,
              );
              return (
                layerToggleToUse[associatedLayerName] &&
                (toggleKey
                  ? layerToggleToUse[toggleKey]
                  : layerToggleToUse[layer.key])
              );
            } else if (availableLayers[layer.key].selectPosition) {
              const associatedLayerName = Object.keys(availableLayers).find(
                (layerName) =>
                  availableLayers[layerName]?.selectPosition ===
                    availableLayers[layer.key].selectPosition &&
                  !!availableLayers[layerName]?.selectKey,
              );
              return (
                layerToggleToUse[associatedLayerName] &&
                (toggleKey
                  ? layerToggleToUse[toggleKey]
                  : layerToggleToUse[layer.key])
              );
            } else {
              return toggleKey
                ? (layerToggleToUse[toggleKey] ?? layerToggleToUse[layer.key])
                : layerToggleToUse[layer.key];
            }
          })
        : layers_and_legends.layers,
    [layers_and_legends.layers, layerToggleToUse, availableLayers],
  );

  const layerSelectionDependencies = useMemo(
    () => layers_and_legends.selectionDependencies,
    [layers_and_legends],
  );

  function addSourcesAndSupps() {
    const existing_sources = map.getStyle().sources;

    if (!pmtilesSources.isLoading && pmtilesSources.sources) {
      for (const source of Object.entries(pmtilesSources.sources)) {
        if (!Object.keys(existing_sources).includes(source[0])) {
          map.addSource(source[0], {
            type: "vector",
            url: "pmtiles://" + source[1],
          });
        }
      }
    }
  }

  const updateLayers = useCallback(
    (layersToAdd) => {
      for (const layerId of layersRef.current) {
        try {
          map.removeLayer(layerId);
        } catch (e) {
          // Layer may not exist on the map (e.g. failed to add previously)
        }
      }
      for (const layer of layersToAdd) {
        try {
          map.addLayer(layer);
        } catch (e) {
          console.error(`Failed to add layer ${layer.id}:`, e);
        }
      }
      layersRef.current = layersToAdd.map((l) => l.id);
    },
    [map, pmtilesSources.isLoading, pmtilesSources.layerInfo],
  );

  useEffect(() => {
    if (!mapLoaded) return;

    const customLayerIds = new Set(layersRef.current);

    const styleLoadHandler = () => {
      addSourcesAndSupps();
      updateLayers(layers);
    };

    map.setStyle(style, {
      transformStyle: (previousStyle, nextStyle) => {
        const updatedStyle = { ...nextStyle };

        // Filter to only preserve custom layers (those we added programmatically)
        const customLayers = (previousStyle.layers || []).filter((layer) =>
          customLayerIds.has(layer.id),
        );

        // Identify custom sources (sources not in the new basemap style)
        // These are sources we added programmatically (like PMTiles)
        const nextStyleSourceIds = new Set(
          Object.keys(nextStyle.sources || {}),
        );
        const customSources = Object.fromEntries(
          Object.entries(previousStyle.sources || {}).filter(
            ([sourceId]) => !nextStyleSourceIds.has(sourceId),
          ),
        );

        updatedStyle.sources = {
          ...nextStyle.sources,
          ...customSources,
        };

        // Add custom layers after the new style's layers so they appear on top
        updatedStyle.layers = [...(nextStyle.layers || []), ...customLayers];

        return updatedStyle;
      },
    });

    map.once("style.load", styleLoadHandler);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [style]);

  useEffect(() => {
    if (!mapLoaded) return;
    setSubgroupOn(
      layers.map((l) => l.id).filter((l) => l.includes("flooding")).length > 0,
    );
    addSourcesAndSupps();
    updateLayers(layers);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [mapLoaded, layers, pmtilesSources.isLoading]);

  return {
    layerGroup,
    layerSelectionDependencies,
    subgroup,
    subgroupOn,
    setLayerGroup,
    setSubgroup,
    layersToggle: layerToggleToUse,
    toggleLayer,
  };
}
